import type { ReactNode } from "react";
import { problem } from "@/data/showcase";

const PHOTOS = [
  "/showcase/case-approved-before.jpg",
  "/showcase/case-approved-after.jpg",
  "/showcase/case-obstructed-before.jpg",
  "/showcase/case-obstructed-after.jpg",
  "/showcase/case-reposition-last.jpg",
  "/showcase/case-reposition-new.jpg",
  "/showcase/case-skip-before.jpg",
  "/showcase/case-skip-after.jpg",
  "/showcase/case-worker-before.jpg",
  "/showcase/case-worker-after.jpg",
];

export function Problem() {
  return (
    <section className="relative bg-[#06080F] border-t border-ink-900 min-h-screen overflow-hidden flex flex-col">
      <ChaosBedWithHeadline>
        <Eyebrow />
        <h2 className="font-serif text-white leading-[0.95] mt-4 text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-[20ch]">
          {problem.titleStart}{" "}
          <span className="text-foton-lighter italic font-display">
            {problem.titleItalic}
          </span>
        </h2>
      </ChaosBedWithHeadline>

      <BottomShell>
        {/* Editorial moment: pitch copy + full-width photo pair as answer */}
        <div className="max-w-[640px]">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-foton-lighter mb-4">
            z Fotonem
          </p>
          <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-[1.15] mb-6">
            {problem.pitchTitle}{" "}
            <span className="text-foton-lighter italic font-display">
              {problem.pitchItalic}
            </span>
          </p>
        </div>

        {/* The actual answer: realny before/after z gładzi ścian salonu. */}
        <div className="mt-10 md:mt-12 mb-10 md:mb-12 max-w-[860px]">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500 mb-3">
            CAM-0002 · salon · gładź ścian · 19.05.2026
          </p>
          <div className="grid grid-cols-2 gap-3">
            <figure>
              <img
                src="/showcase/case-approved-before.jpg"
                alt="Stan poprzedni"
                loading="lazy"
                className="w-full aspect-[16/10] object-cover"
              />
              <figcaption className="font-mono text-[10px] uppercase tracking-wider text-ink-600 mt-2">
                15.05 · before
              </figcaption>
            </figure>
            <figure>
              <img
                src="/showcase/case-approved-after.jpg"
                alt="Stan zaproponowany"
                loading="lazy"
                className="w-full aspect-[16/10] object-cover"
              />
              <figcaption className="font-mono text-[10px] uppercase tracking-wider text-foton-lighter mt-2">
                19.05 · after · +8% postępu
              </figcaption>
            </figure>
          </div>
        </div>

        {/* Body — 2-col editorial paragraph */}
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 text-base text-ink-300 leading-relaxed max-w-[860px]">
          {problem.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </BottomShell>
    </section>
  );
}

function Eyebrow() {
  return (
    <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-foton-lighter font-semibold">
      {problem.eyebrow}
    </p>
  );
}

function ChaosBedWithHeadline({ children }: { children: ReactNode }) {
  return (
    <div className="relative h-[55vh] overflow-hidden border-b border-ink-800">
      <div className="absolute inset-0 grid grid-cols-8 grid-rows-4 gap-1">
        {Array.from({ length: 32 }, (_, i) => (
          <img
            key={i}
            src={PHOTOS[i % PHOTOS.length]}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
            style={{ opacity: 0.35, filter: "grayscale(1)" }}
          />
        ))}
      </div>
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, rgba(6,8,15,0.85) 0%, rgba(6,8,15,0.5) 40%, rgba(6,8,15,0.95) 100%)",
        }}
      />
      <div className="relative h-full flex flex-col justify-center max-w-[1320px] mx-auto px-6">
        {children}
      </div>
      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{
          background: "linear-gradient(to right, transparent 0%, #BB97FF 50%, transparent 100%)",
          boxShadow: "0 0 20px #8D51FF",
        }}
      />
    </div>
  );
}

function BottomShell({ children }: { children: ReactNode }) {
  return (
    <div className="relative py-12 md:py-20 flex-1">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 50% 70% at 50% 50%, rgba(141,81,255,0.15), transparent 70%)",
        }}
      />
      <div className="relative mx-auto max-w-[1080px] px-6">
        {children}
      </div>
    </div>
  );
}

